import React from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Spinner,
  Table,
} from 'react-bootstrap';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import useHeader from 'containers/App/Header/useHeader';
import {
  ApplicationCredential,
  ApplicationCredentialInput,
  useCreateApplicationCredentialMutation,
  useDeleteApplicationCredentialMutation,
  useGetApplicationCredentialsQuery,
  useUpdateApplicationCredentialMutation,
} from 'services/api/adminApplicationCredentials';
import { ApplicationCredentialManagementPageStyles } from './ApplicationCredentialManagementPageStyles';

const EMPTY_FORM: ApplicationCredentialInput = {
  application: '',
  country: '',
  user: '',
  password: '',
};

const getErrorMessage = (error: any, fallback: string) =>
  String(
    error?.data?.error ||
      error?.data?.message ||
      error?.error ||
      error?.message ||
      fallback,
  );

const formatDateTime = (value?: string | null) => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString();
};

const hasPlainNameCharacters = (value: string) => /^[A-Za-z0-9 ]+$/.test(value);

const hasValidEmailFormat = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const REDACTED_PASSWORD_DISPLAY = '********';

const getCredentialValidationError = (
  input: ApplicationCredentialInput,
  options: { requirePassword: boolean } = { requirePassword: true },
) => {
  if (!input.application.trim()) return 'Application is required.';
  if (!input.country.trim()) return 'Country is required.';
  if (!input.user.trim()) return 'User is required.';
  if (options.requirePassword && !input.password) return 'Password is required.';
  if (!hasPlainNameCharacters(input.application.trim())) {
    return 'Application can only contain letters, numbers, and spaces.';
  }
  if (!hasPlainNameCharacters(input.country.trim())) {
    return 'Country can only contain letters, numbers, and spaces.';
  }
  if (!hasValidEmailFormat(input.user.trim())) {
    return 'User must be a valid email address.';
  }
  return '';
};

function PasswordDisplay() {
  return (
    <Form.Control
      readOnly
      size="sm"
      className="credential-password-cell"
      type="text"
      value={REDACTED_PASSWORD_DISPLAY}
      aria-label="Credential password redacted"
    />
  );
}

function CredentialModal({
  credential,
  show,
  isSaving,
  onHide,
  onSubmit,
}: {
  credential: ApplicationCredential | null;
  show: boolean;
  isSaving: boolean;
  onHide: () => void;
  onSubmit: (input: ApplicationCredentialInput) => Promise<void>;
}) {
  const [form, setForm] = React.useState<ApplicationCredentialInput>(EMPTY_FORM);
  const [validationError, setValidationError] = React.useState('');
  const isEditing = Boolean(credential);

  React.useEffect(() => {
    if (!show) return;

    setForm(
      credential
        ? {
            application: credential.application,
            country: credential.country,
            user: credential.user,
            password: '',
          }
        : EMPTY_FORM,
    );
    setValidationError('');
  }, [credential, show]);

  const updateField = (field: keyof ApplicationCredentialInput) => event => {
    setForm(current => ({
      ...current,
      [field]: event.target.value,
    }));
    setValidationError('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const nextInput = {
      application: form.application.trim(),
      country: form.country.trim(),
      user: form.user.trim(),
      password: form.password,
    };
    const nextValidationError = getCredentialValidationError(nextInput, {
      requirePassword: !isEditing,
    });

    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    await onSubmit(nextInput);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Edit Credential' : 'Add Credential'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationError ? (
            <Alert variant="danger" className="mb-3">
              {validationError}
            </Alert>
          ) : null}
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="credential-filter-label">
                Country
              </Form.Label>
              <Form.Control
                required
                pattern="[A-Za-z0-9 ]+"
                title="Country can only contain letters, numbers, and spaces."
                value={form.country}
                onChange={updateField('country')}
                placeholder="Country"
              />
            </Col>
            <Col md={6}>
              <Form.Label className="credential-filter-label">
                Application
              </Form.Label>
              <Form.Control
                required
                pattern="[A-Za-z0-9 ]+"
                title="Application can only contain letters, numbers, and spaces."
                value={form.application}
                onChange={updateField('application')}
                placeholder="Application"
              />
            </Col>
            <Col md={12}>
              <Form.Label className="credential-filter-label">User</Form.Label>
              <Form.Control
                required
                type="email"
                value={form.user}
                onChange={updateField('user')}
                placeholder="user@example.com"
              />
            </Col>
            <Col md={12}>
              <Form.Label className="credential-filter-label">
                Password
              </Form.Label>
              <Form.Control
                required={!isEditing}
                type="password"
                value={form.password}
                onChange={updateField('password')}
                placeholder={
                  isEditing ? 'Leave blank to keep existing password' : 'Password'
                }
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            className="credential-submit"
            variant="primary"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving
              </>
            ) : (
              'Save Credential'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default function ApplicationCredentialManagementPage() {
  useHeader('Application Credentials');

  const [applicationFilter, setApplicationFilter] = React.useState('');
  const [countryFilter, setCountryFilter] = React.useState('');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingCredential, setEditingCredential] =
    React.useState<ApplicationCredential | null>(null);

  const {
    data = [],
    error,
    isFetching,
    isLoading,
  } = useGetApplicationCredentialsQuery();
  const [createCredential, createState] =
    useCreateApplicationCredentialMutation();
  const [updateCredential, updateState] =
    useUpdateApplicationCredentialMutation();
  const [deleteCredential, deleteState] =
    useDeleteApplicationCredentialMutation();

  const filteredCredentials = React.useMemo(() => {
    return data.filter(item => {
      const applicationMatched =
        !applicationFilter || item.application === applicationFilter;
      const countryMatched = !countryFilter || item.country === countryFilter;
      return applicationMatched && countryMatched;
    });
  }, [applicationFilter, countryFilter, data]);

  const sortedCredentials = React.useMemo(
    () =>
      [...filteredCredentials].sort(
        (left, right) =>
          new Date(right.updatedAt || right.createdAt || 0).getTime() -
          new Date(left.updatedAt || left.createdAt || 0).getTime(),
      ),
    [filteredCredentials],
  );
  const applicationOptions = React.useMemo(
    () => Array.from(new Set(data.map(item => item.application))).sort(),
    [data],
  );
  const countryOptions = React.useMemo(
    () => Array.from(new Set(data.map(item => item.country))).sort(),
    [data],
  );
  const isSaving = createState.isLoading || updateState.isLoading;

  const openCreateModal = () => {
    setEditingCredential(null);
    setModalOpen(true);
  };

  const openEditModal = (credential: ApplicationCredential) => {
    setEditingCredential(credential);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalOpen(false);
    setEditingCredential(null);
  };

  const handleSubmit = async (input: ApplicationCredentialInput) => {
    try {
      if (editingCredential) {
        await updateCredential({
          id: editingCredential.id,
          ...input,
        }).unwrap();
        toast.success('Credential updated.');
      } else {
        await createCredential(input).unwrap();
        toast.success('Credential added.');
      }
      closeModal();
    } catch (mutationError) {
      toast.error(
        getErrorMessage(mutationError, 'Unable to save credential right now.'),
      );
    }
  };

  const handleDelete = async (credential: ApplicationCredential) => {
    const confirmed = window.confirm(
      `Delete the ${credential.application} credential for ${credential.country}?`,
    );

    if (!confirmed) return;

    try {
      await deleteCredential(credential.id).unwrap();
      toast.success('Credential deleted.');
    } catch (mutationError) {
      toast.error(
        getErrorMessage(mutationError, 'Unable to delete credential right now.'),
      );
    }
  };

  return (
    <ApplicationCredentialManagementPageStyles>
      <div className="credential-shell">
        <div className="credential-header">
          <div>
            <div className="credential-eyebrow">Admin Center</div>
            <h2 className="credential-title">Application Credentials</h2>
            <p className="credential-subtitle">
              Manage country-level application credentials used by operational
              testing workflows.
            </p>
          </div>
          <Button
            className="credential-add-button d-inline-flex align-items-center gap-2"
            variant="primary"
            onClick={openCreateModal}
          >
            <BsPlus />
            Add Credential
          </Button>
        </div>

        <div className="credential-filter-panel">
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label className="credential-filter-label">
                Application
              </Form.Label>
              <Form.Select
                value={applicationFilter}
                onChange={event => setApplicationFilter(event.target.value)}
              >
                <option value="">All Applications</option>
                {applicationOptions.map(application => (
                  <option key={application} value={application}>
                    {application}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="credential-filter-label">
                Country
              </Form.Label>
              <Form.Select
                value={countryFilter}
                onChange={event => setCountryFilter(event.target.value)}
              >
                <option value="">All Countries</option>
                {countryOptions.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setApplicationFilter('');
                  setCountryFilter('');
                }}
                disabled={!applicationFilter && !countryFilter}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </div>

        <div className="credential-panel">
          <div className="credential-panel-header">
            <h3 className="credential-panel-title">
              Credentials ({sortedCredentials.length})
            </h3>
            {isFetching ? (
              <span className="text-muted small">
                <Spinner animation="border" size="sm" className="me-2" />
                Refreshing
              </span>
            ) : null}
          </div>

          {isLoading ? (
            <div className="credential-loading-state">
              <Spinner animation="border" className="me-2" />
              Loading credentials...
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-3">
              Unable to load application credentials.
            </Alert>
          ) : sortedCredentials.length === 0 ? (
            <div className="credential-empty-state">
              No credentials matched the selected filters.
            </div>
          ) : (
            <div className="credential-table-scroll">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Application</th>
                    <th>User</th>
                    <th>Password</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCredentials.map(credential => (
                    <tr key={credential.id}>
                      <td className="fw-semibold">{credential.country}</td>
                      <td>{credential.application}</td>
                      <td>{credential.user}</td>
                      <td>
                        <PasswordDisplay />
                      </td>
                      <td>{formatDateTime(credential.updatedAt)}</td>
                      <td className="text-end credential-actions">
                        <div className="d-inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => openEditModal(credential)}
                          >
                            <MdEdit />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="d-inline-flex align-items-center gap-1"
                            disabled={deleteState.isLoading}
                            onClick={() => handleDelete(credential)}
                          >
                            <BsTrash />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>

        <CredentialModal
          credential={editingCredential}
          show={modalOpen}
          isSaving={isSaving}
          onHide={closeModal}
          onSubmit={handleSubmit}
        />
      </div>
    </ApplicationCredentialManagementPageStyles>
  );
}
